import dayjs from 'dayjs'
import parsePhoneNumber from 'libphonenumber-js'

import { formatAmount } from '@utils'
import { OrderType } from '@schemas'
import {
	downloadOrderDocument,
	OrderDocumentModel,
	OrderDocumentRow,
} from './orderDocument'
import { downloadOrdersExcel, todayStamp } from './orderExcel'

/**
 * My Order(자사몰 주문)의 표기 — 화면 목록 · 자세히 · 엑셀 · PDF 가 같이 쓴다 (2026-10-06).
 * 상태 이름 · 주소 · 연락처 계산은 옛 My Order 페이지의 식 그대로다.
 */

/** 주문이 청구된 통화로 찍는다(USD · KRW). 옛 주문은 스키마가 USD 컬럼으로 되살린 값이다 */
export const retailMoney = (currency: string, value: number): string =>
	formatAmount(currency, value)

/**
 * 프리오더 주문의 주문확인 뒤 · 송장 전 구간 (ORDER_PLAN §3.1). 날짜가 기입된 가장 늦은 단계가 현재 상태다.
 * 배송중 · 완료는 이 주문의 송장 · 완료일로만 정한다 — 배치 날짜로 만들지 않는다.
 */
const preorderPreparingTitle = (order: OrderType): string =>
	order.fulfillmentStage === 'delivery_start' ||
	order.fulfillmentStage === 'delivery_done'
		? 'Preparing for transport'
		: order.fulfillmentStage === 'production'
		? 'In production'
		: order.fulfillmentStage === 'part_order'
		? 'Parts ordered'
		: 'Pre-order confirmed'

export const retailStatusTitle = (order: OrderType): string =>
	order.status === 'new'
		? 'Paid in full'
		: order.status === 'preparing'
		? order.hasPreorderLine
			? preorderPreparingTitle(order)
			: 'Preparing for transport'
		: order.status === 'inTransit'
		? 'In transit'
		: order.status === 'completed'
		? 'Delivery completed'
		: order.status === 'deleted'
		? 'Deleted'
		: order.status === 'refunded'
		? 'Refunded'
		: 'Payment completed'

/**
 * 고객 자세히의 프리오더 진행 줄 — 기입된 단계만, 날짜와 함께. 출고 종료(delivery_done)는 배치 단위라
 * 이 주문의 출고와 다르므로 고객에게 보이지 않는다. 주문확인 전이면 비운다(배치 단계는 확인 뒤에만).
 */
export const retailPreorderSteps = (order: OrderType): [string, string][] =>
	order.hasPreorderLine && order.preparedAt !== ''
		? (
				[
					['part_order', 'Parts ordered'],
					['production', 'Production started'],
					['delivery_start', 'Shipping started'],
				] as const
		  )
				.filter(([aStage]) => (order.fulfillmentDates?.[aStage] ?? '') !== '')
				.map(([aStage, aLabel]) => [aLabel, retailDate(order.fulfillmentDates[aStage])])
		: []

export const retailAddress = (order: OrderType): string =>
	[order.address1, order.address2, order.city, order.state, order.country]
		.filter(
			aPart =>
				aPart !== undefined &&
				aPart !== null &&
				!['', 'undefined'].includes(String(aPart)),
		)
		.join(', ')

export const retailPhone = (order: OrderType): string =>
	parsePhoneNumber(
		'+' + order.phoneCountryNumber + order.phoneNumber,
	)?.formatInternational() ?? ''

/** 품목 이름 — 옵션 · 가격 단계까지 */
export const retailItemName = (anItem: OrderType['detailList'][number]): string =>
	`${anItem.productName}${anItem.optionName ? ` · ${anItem.optionName}` : ''}${
		anItem.pricePhase === 'earlybird'
			? ' · Early bird'
			: anItem.pricePhase === 'preorder'
			? ' · Pre-order'
			: ''
	}`

export const retailDate = (value: string): string =>
	value ? dayjs(value).format('MMM DD YYYY') : ''

const carrierOf = (order: OrderType): string =>
	order.deliveryCompany === 'local' ? '' : order.deliveryCompany ?? ''

export const retailOrderDocumentModel = (order: OrderType): OrderDocumentModel => {
	const money = (v: number) => retailMoney(order.currency, v)
	const payment: OrderDocumentRow[] = [
		['Subtotal', money(order.totalAmount)],
		...(order.discountAmount > 0
			? [
					[
						`Coupon${order.couponCode ? ` · ${order.couponCode}` : ''}`,
						`- ${money(order.discountAmount)}`,
					] as OrderDocumentRow,
			  ]
			: []),
		['Shipping fee', money(order.shippingAmount)],
		[`Total paid (${order.currency})`, money(order.paidAmount), true],
		...(order.refundAmount > 0
			? [
					[
						`Refunded${order.refundedAt ? ` · ${retailDate(order.refundedAt)}` : ''}`,
						`- ${money(order.refundAmount)}`,
					] as OrderDocumentRow,
			  ]
			: []),
	]
	const carrier = carrierOf(order)
	return {
		kind: 'ORDER',
		id: order.id,
		facts: [
			`Placed ${dayjs(order.createdAt).format('MMM DD YYYY · HH:mm')}`,
			retailStatusTitle(order),
			order.currency,
		].join('   ·   '),
		items: order.detailList.map(anItem => {
			const unit = anItem.unitAmount + anItem.optionAmount
			return {
				name: retailItemName(anItem),
				unit: money(unit),
				qty: String(anItem.quantity),
				total: money(unit * anItem.quantity),
			}
		}),
		sections: [
			{ title: 'Payment', rows: payment, alignRight: true },
			{
				title: 'Recipient',
				rows: [
					[
						'Name',
						`${order.receiverGivenName ?? ''} ${order.receiverFamilyName ?? ''}`.trim(),
					],
					['Address', retailAddress(order)],
					['Contact', retailPhone(order)],
				],
				alignRight: false,
			},
			{
				title: 'Shipping',
				rows: [
					['Carrier', carrier !== '' ? carrier : '-'],
					['Tracking No.', order.trackingNumber ? order.trackingNumber : '-'],
					...(order.deliveryCompletedAt
						? [['Delivered', retailDate(order.deliveryCompletedAt)] as OrderDocumentRow]
						: []),
				],
				alignRight: false,
			},
		],
	}
}

export const downloadRetailOrderDocument = (order: OrderType): Promise<void> =>
	downloadOrderDocument(retailOrderDocumentModel(order), `judahs-order-${order.id}.pdf`)

export const downloadRetailOrdersExcel = (orders: OrderType[]): Promise<void> =>
	downloadOrdersExcel({
		rows: orders.map(anOrder => ({
			id: anOrder.id,
			date: anOrder.createdAt ? dayjs(anOrder.createdAt).format('YYYY-MM-DD') : '',
			status: retailStatusTitle(anOrder),
			amount: Number(anOrder.paidAmount ?? 0),
			currency: anOrder.currency,
		})),
		sheetName: 'Orders',
		fileName: `judahs-orders-${todayStamp()}.xlsx`,
	})
